import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2, Minimize2, X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Overlay } from '@/components/Overlay';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

/**
 * Sterowane rozszerzenie (`expanded` + `onExpandedChange` RAZEM): okno, którego treść musi znać
 * tryb (np. lista wyniku wypełniająca wysokość = jeden scroll), trzyma stan u siebie. Bez
 * `expanded` stan jest wewnętrzny (zerowany przy zamknięciu), a `onExpandedChange` jest tylko
 * powiadomieniem. `expanded` bez `onExpandedChange` dałby martwy przycisk — typ tego zabrania.
 */
type ExpandControl =
  | { expanded?: undefined; onExpandedChange?: (expanded: boolean) => void }
  | { expanded: boolean; onExpandedChange: (expanded: boolean) => void };

export type ModalProps = ModalBaseProps & ExpandControl;

interface ModalBaseProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  hideCloseButton?: boolean;
  role?: 'dialog' | 'alertdialog';
  /**
   * Stała wysokość panelu (85vh) zamiast dopasowania do treści. Dla modali z zakładkami,
   * gdzie każda zakładka ma inną wysokość treści — bez tego modal „skacze" przy przełączaniu.
   * Treść krótsza od panelu pozostawia miejsce; dłuższa scrolluje w obrębie ciała modala.
   */
  fixedHeight?: boolean;
  /**
   * Dodaje w nagłówku przycisk „rozszerz/zwiń" — przełącza panel między rozmiarem `size`
   * a prawie-pełnym ekranem (95vw × 95vh). Dla modali osadzających duże kreatory
   * (builder formularza/listy), gdzie domyślna szerokość bywa za ciasna.
   */
  expandable?: boolean;
  /** `data-testid` na tle okna (zawiera cały panel — zapytania `within(...)` działają). */
  testId?: string;
  /**
   * Zabezpieczenie niezapisanych zmian dla dużych formularzy: zamknięcie kliknięciem w tło lub
   * Esc po wpisaniu czegokolwiek pyta „Porzucić wprowadzone zmiany?". X i przyciski w oknie nie
   * pytają. Szczegóły i ograniczenia → `Overlay`.
   */
  guardUnsaved?: boolean;
  /** Jawna informacja „są niezapisane zmiany" (nadpisuje wykrywanie wpisywania). */
  dirty?: boolean;
  /** Zmiana wartości zeruje wykryte „coś wpisano" (okno zostaje otwarte po zapisie). */
  guardResetKey?: string | number;
}

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  /** Prawie cała szerokość okna — szerokie tabele (np. okno wyboru rekordu z wieloma kolumnami). */
  full: 'max-w-[95vw]',
};

/**
 * Modal — zwykłe okno dialogowe (wygląd). Zachowanie (zamykanie tłem i Esc, pułapka fokusu,
 * blokada przewijania, portal) daje wspólny rdzeń `Overlay`.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  hideCloseButton,
  role = 'dialog',
  fixedHeight = false,
  expandable = false,
  expanded: expandedProp,
  onExpandedChange,
  testId,
  guardUnsaved,
  dirty,
  guardResetKey,
}: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const [expandedState, setExpandedState] = useState(false);
  const controlled = expandedProp !== undefined;
  const expanded = controlled ? expandedProp : expandedState;
  const setExpanded = (next: boolean) => {
    if (controlled) onExpandedChange?.(next);
    else {
      setExpandedState(next);
      onExpandedChange?.(next);
    }
  };

  // Zamknięcie modala resetuje rozszerzenie — kolejne otwarcie startuje w rozmiarze `size`.
  // W trybie sterowanym reset należy do właściciela stanu.
  useEffect(() => {
    if (!open && !controlled) setExpandedState(false);
  }, [open, controlled]);

  return (
    <Overlay
      open={open}
      onClose={onClose}
      closeOnBackdrop={closeOnBackdrop}
      closeOnEsc={closeOnEsc}
      role={role}
      backdropClassName="z-50 flex items-center justify-center bg-black/50 p-4"
      panelClassName={[
        // Miękka poświata zamiast twardego shadow-xl (decyzja 2026-07-02,
        // wzór z makiety podglądu) — spójnie z PreviewModal.
        'w-full rounded-2xl border border-border-secondary bg-bg-elevated',
        'shadow-[0_18px_50px_rgba(31,41,55,0.28)] dark:shadow-[0_18px_50px_rgba(0,0,0,0.6)]',
        'flex flex-col',
        expanded ? 'h-[95vh] max-h-[95vh] max-w-[95vw]' : 'max-h-[90vh]',
        expanded ? '' : fixedHeight ? 'h-[85vh]' : '',
        expanded ? '' : SIZE[size],
      ].join(' ')}
      labelledBy={title ? titleId : undefined}
      describedBy={description ? descId : undefined}
      testId={testId}
      guardUnsaved={guardUnsaved}
      dirty={dirty}
      guardResetKey={guardResetKey}
    >
      {(title || !hideCloseButton || expandable) && (
        <header className="flex items-start justify-between gap-4 border-b border-border-secondary px-5 py-4">
          <div className="min-w-0">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold text-text-primary">
                {title}
              </h2>
            )}
            {description && (
              <p id={descId} className="mt-1 text-sm text-text-tertiary">
                {description}
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {expandable && (
              <IconButton
                size="sm"
                variant="ghost"
                onClick={() => setExpanded(!expanded)}
                aria-label={expanded ? t('common.collapse') : t('common.expand')}
                title={expanded ? t('common.collapse') : t('common.expand')}
              >
                {expanded ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
              </IconButton>
            )}
            {!hideCloseButton && (
              <IconButton
                size="sm"
                variant="ghost"
                onClick={onClose}
                aria-label={t('common.close')}
              >
                <X aria-hidden="true" />
              </IconButton>
            )}
          </div>
        </header>
      )}
      {children != null && (
        <div className="flex-1 overflow-y-auto px-5 py-4 text-sm text-text-primary">{children}</div>
      )}
      {footer && (
        <footer className="flex items-center justify-end gap-2 border-t border-border-secondary px-5 py-3">
          {footer}
        </footer>
      )}
    </Overlay>
  );
}
